// src/components/parent-dashboard/ParentDashboard.js
import React, { useEffect, useState } from "react";
import Layout from "../../Layout";
import apiService from "./api/apiService";
import preparatoryApiService from "./api/preparatoryApiService"; // Import preparatory API service
import middleApiService from "./api/middleApiService"; // Import middle stage API service
import { detectGrade, getApiServiceForGrade, isMiddleStageGrade, isPreparatoryGrade } from "./utils/gradeUtils";
import Notification from "./components/Notification";
import DashboardView from "./views/DashboardView";
import ChildDetailView from "./views/ChildDetailView";
import AssignmentDetailView from "./views/AssignmentDetailView";
import AttendanceDetailView from "./views/AttendanceDetailView";

// API base URL
import { API_BASE_URL } from "../../config";

/**
 * Parent Dashboard Main Component
 * Manages state and data fetching for the entire parent dashboard
 */
const ParentDashboard = () => {
    // View state
    const [currentView, setCurrentView] = useState("dashboard"); // dashboard, childDetail, assignmentDetail, attendanceDetail

    // Data state
    const [children, setChildren] = useState([]);
    const [selectedChild, setSelectedChild] = useState(null);
    const [selectedChildClasses, setSelectedChildClasses] = useState([]);
    const [selectedChildTeachers, setSelectedChildTeachers] = useState([]);
    const [selectedChildProgress, setSelectedChildProgress] = useState(null);
    const [selectedChildAssignments, setSelectedChildAssignments] = useState([]);
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const [selectedChildAttendance, setSelectedChildAttendance] = useState([]);
    const [selectedClassAttendance, setSelectedClassAttendance] = useState(null);
    const [selectedClass, setSelectedClass] = useState(null);
    const [childProfilePicture, setChildProfilePicture] = useState(null);
    const [detectedGrade, setDetectedGrade] = useState(null);

    // Parent profile picture state
    const [parentProfilePicture, setParentProfilePicture] = useState(null);
    const [showParentProfilePictureModal, setShowParentProfilePictureModal] = useState(false);
    const [uploadingParentPicture, setUploadingParentPicture] = useState(false);

    // UI state
    const [searchQuery, setSearchQuery] = useState("");
    const [linkEmail, setLinkEmail] = useState("");
    const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
    const [showProfilePictureModal, setShowProfilePictureModal] = useState(false);
    const [uploadingPicture, setUploadingPicture] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notification, setNotification] = useState({ show: false, message: "", type: "" });
    const [messages, setMessages] = useState([]);

    // Caregiver observation form state
    const [observationForm, setObservationForm] = useState({
        resources: [],
        other_resources: "",
        assessment: {
            finds_school_safe: "Not sure",
            participates_in_activities: "Not sure",
            finds_curriculum_difficult: "Not sure",
            making_good_progress: "Not sure",
            gets_school_support: "Not sure",
            can_talk_feelings: "Not sure",
            can_calm_down: "Not sure",
            can_understand_friends: "Not sure",
            respects_opinions: "Not sure",
            helps_after_fight: "Not sure",
            comforts_when_sad: "Not sure"
        },
        needs_support: [],
        other_support: "",
        // Add fields for middle stage if needed
        understanding: {
            motivated_to_learn: "Not sure",
            follows_schedule: "Not sure",
            finds_curriculum_difficult: "Not sure",
            making_good_progress: "Not sure"
        },
        school_support_needs: [],
        other_school_support: "",
        home_support_plan: ""
    });

    // Initial data fetch
    useEffect(() => {
        fetchChildren();
        fetchParentProfilePicture(); // Fetch parent profile picture on component mount
    }, []);

    // Fetch children when search query changes
    useEffect(() => {
        if (children.length > 0) {
            filterChildren();
        }
    }, [searchQuery, children]);

    // Fetch child details when a child is selected
    useEffect(() => {
        if (selectedChild && currentView === "childDetail") {
            fetchChildDetails(selectedChild.student_id || selectedChild.id);
        }
    }, [selectedChild, currentView]);

    // Fetch child's submission when an assignment is selected
    useEffect(() => {
        if (selectedAssignment && currentView === "assignmentDetail") {
            fetchSubmission(selectedChild.student_id || selectedChild.id, selectedAssignment.assignment_id || selectedAssignment.id);
        }
    }, [selectedAssignment, currentView]);

    // Fetch attendance overview when a class is selected
    useEffect(() => {
        if (selectedClass && currentView === "attendanceDetail") {
            fetchAttendanceOverview(selectedChild.student_id || selectedChild.id, selectedClass.class_id || selectedClass.id);
        }
    }, [selectedClass, currentView]);

    // Fetch profile picture when child is selected
    useEffect(() => {
        if (selectedChild && currentView === "childDetail") {
            fetchChildProfilePicture(selectedChild.student_id || selectedChild.id);
        }
    }, [selectedChild, currentView]);

    // Fetch all children
    const fetchChildren = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await apiService.getChildren();
            setChildren(data);
        } catch (err) {
            setError("Failed to fetch children. Please try again.");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Filter children based on search query
    const filterChildren = () => {
        // No implementation needed for small number of children
        // This would filter the children array if we had a large number
    };

    // Get filtered children array
    const getFilteredChildren = () => {
        return children.filter(child => {
            const query = searchQuery.trim().toLowerCase();
            if (!query) return true; // Show all if no search
            return (
                (child.name && child.name.toLowerCase().includes(query)) ||
                (child.school && child.school.toLowerCase().includes(query))
            );
        });
    };

    // Fetch parent profile picture
    const fetchParentProfilePicture = async () => {
        try {
            const result = await apiService.getParentProfilePicture();
            setParentProfilePicture(result?.profile_picture_url ? API_BASE_URL + result.profile_picture_url : null);
        } catch (error) {
            // Profile picture not found is okay, just use initials
            setParentProfilePicture(null);
            console.log("Parent profile picture not found, using default");
        }
    };

    // Handle parent profile picture upload
    const handleParentProfilePictureUpload = async (file) => {
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
            showNotification("Please upload a valid image file (JPEG, PNG, or GIF)", "error");
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            showNotification("File size must be less than 5MB", "error");
            return;
        }

        setUploadingParentPicture(true);
        try {
            const result = await apiService.uploadParentProfilePicture(file);
            setParentProfilePicture(API_BASE_URL + result.profile_picture_url);
            setShowParentProfilePictureModal(false);
            showNotification("Profile picture updated successfully!", "success");
        } catch (error) {
            showNotification(`Failed to upload profile picture: ${error.message}`, "error");
        } finally {
            setUploadingParentPicture(false);
        }
    };

    // Fetch child profile picture
    const fetchChildProfilePicture = async (childId) => {
        try {
            const result = await apiService.getChildProfilePicture(childId);
            setChildProfilePicture(result?.profile_picture_url ? API_BASE_URL + result.profile_picture_url : null);
        } catch (error) {
            // Profile picture not found is okay, just use initials
            setChildProfilePicture(null);
        }
    };

    // Handle profile picture upload
    const handleProfilePictureUpload = async (file) => {
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
            showNotification("Please upload a valid image file (JPEG, PNG, or GIF)", "error");
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            showNotification("File size must be less than 5MB", "error");
            return;
        }

        setUploadingPicture(true);
        try {
            const result = await apiService.uploadChildProfilePicture(selectedChild.student_id || selectedChild.id, file);
            setChildProfilePicture(API_BASE_URL + result.profile_picture_url);
            setShowProfilePictureModal(false);
            showNotification("Profile picture updated successfully!", "success");
        } catch (error) {
            showNotification(`Failed to upload profile picture: ${error.message}`, "error");
        } finally {
            setUploadingPicture(false);
        }
    };

    // Fetch child's details (classes, teachers, progress, assignments)
    const fetchChildDetails = async (childId) => {
        setLoading(true);
        try {
            const [classes, teachers, progress] = await Promise.all([
                apiService.getChildClasses(childId),
                apiService.getChildTeachers(childId),
                apiService.getChildProgress(childId)
            ]);

            setSelectedChildClasses(classes);
            setSelectedChildTeachers(teachers);
            setSelectedChildProgress(progress);

            // Detect grade from child and class information
            const grade = detectGrade({
                ...selectedChild,
                classes: classes
            });

            if (grade) {
                console.log(`Detected grade ${grade} for child ${selectedChild.name}`);
                setDetectedGrade(grade);
            }

            // Fetch assignments using the appropriate API service based on grade
            const serviceToUse = getApiServiceForGrade(grade, selectedChild, null);
            const isPrep = isPreparatoryGrade(grade);
            const isMid = isMiddleStageGrade(grade);

            console.log(`Fetching assignments using ${isMid ? 'middle' : isPrep ? 'preparatory' : 'foundation'} API service for grade ${grade}`);

            let assignments;
            if (isMid && serviceToUse.getChildAssignments) {
                assignments = await serviceToUse.getChildAssignments(childId);
            } else if (isPrep && serviceToUse.getChildAssignments) {
                assignments = await serviceToUse.getChildAssignments(childId);
            } else {
                assignments = await apiService.getChildAssignments(childId);
            }

            setSelectedChildAssignments(assignments);

            // Simulate messages for the UI based on assignments
            const mockMessages = assignments.slice(0, 2).map((assignment, idx) => ({
                id: idx + 1,
                from: teachers[idx % teachers.length]?.name || 'Teacher',
                subject: assignment.title || 'Welcome to the new term!',
                date: new Date().toISOString().split('T')[0],
                content: `Dear Parent, regarding the ${assignment.title} assignment due on ${new Date(assignment.due_date).toLocaleDateString()}`,
                unread: idx === 0
            }));
            setMessages(mockMessages);

            // Also fetch attendance data
            try {
                const attendance = await apiService.getChildAttendance(childId);
                setSelectedChildAttendance(attendance);
            } catch (attendanceErr) {
                console.error("Error fetching attendance:", attendanceErr);
                // Don't show notification for attendance errors - they might not have any attendance records yet
                setSelectedChildAttendance([]);
            }
        } catch (err) {
            showNotification("Failed to fetch child's details.", "error");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Determine if the current child is in preparatory stage
    const getIsPreparatory = () => {
        // First try to use the detected grade
        if (detectedGrade !== null) {
            return isPreparatoryGrade(detectedGrade);
        }

        // If no detected grade, try to determine from other sources
        // If no child or classes selected, default to false
        if (!selectedChild || !selectedChildClasses || selectedChildClasses.length === 0) {
            return false;
        }

        // Get grade from the student object from the submission if available
        if (selectedSubmission?.student?.grade) {
            return isPreparatoryGrade(selectedSubmission.student.grade);
        }

        // Otherwise get from classes
        const childClass = selectedChildClasses[0] || {};
        const grade = childClass?.grade;

        // Log for debugging
        console.log(`Checking preparatory status for grade: ${grade}`);

        return isPreparatoryGrade(grade);
    };

    // Determine if the current child is in middle stage
    const getIsMiddleStage = () => {
        // First try to use the detected grade
        if (detectedGrade !== null) {
            return isMiddleStageGrade(detectedGrade);
        }

        // If no detected grade, try to determine from other sources
        // If no child or classes selected, default to false
        if (!selectedChild || !selectedChildClasses || selectedChildClasses.length === 0) {
            return false;
        }

        // Get grade from the student object from the submission if available
        if (selectedSubmission?.student?.grade) {
            return isMiddleStageGrade(selectedSubmission.student.grade);
        }

        // Otherwise get from classes
        const childClass = selectedChildClasses[0] || {};
        const grade = childClass?.grade;

        // Log for debugging
        console.log(`Checking middle stage status for grade: ${grade}`);

        return isMiddleStageGrade(grade);
    };

    // Get the appropriate API service based on grade
    const getApiService = () => {
        // Use the utility function to get the correct API service
        return getApiServiceForGrade(detectedGrade, selectedChild, selectedAssignment);
    };

    // Fetch submission for an assignment
    const fetchSubmission = async (childId, assignmentId) => {
        setLoading(true);
        try {
            // Use the detected grade to determine API service
            const gradeToUse = detectedGrade;
            const serviceToUse = getApiServiceForGrade(gradeToUse, selectedChild, selectedAssignment);
            const isPrep = isPreparatoryGrade(gradeToUse);
            const isMid = isMiddleStageGrade(gradeToUse);

            console.log(`Fetching submission using ${isMid ? 'middle' : isPrep ? 'preparatory' : 'foundation'} API service for grade ${gradeToUse}`);

            const submission = await serviceToUse.getChildSubmission(childId, assignmentId);
            console.log("Received submission:", submission);

            setSelectedSubmission(submission);

            // Pre-fill the observation form based on stage
            if (submission?.caregiver_observation) {
                if (isMid) {
                    // Middle stage form fields
                    setObservationForm({
                        resources: submission.caregiver_observation.resources || [],
                        other_resources: submission.caregiver_observation.other_resources || "",
                        understanding: submission.caregiver_observation.understanding || {
                            motivated_to_learn: "Not sure",
                            follows_schedule: "Not sure",
                            finds_curriculum_difficult: "Not sure",
                            making_good_progress: "Not sure"
                        },
                        school_support_needs: submission.caregiver_observation.school_support_needs || [],
                        other_school_support: submission.caregiver_observation.other_school_support || "",
                        home_support_plan: submission.caregiver_observation.home_support_plan || ""
                    });
                } else if (isPrep) {
                    // Preparatory stage form fields
                    setObservationForm({
                        resources: submission.caregiver_observation.resources || [],
                        other_resources: submission.caregiver_observation.other_resources || "",
                        assessment: submission.caregiver_observation.assessment || {
                            finds_school_safe: "Not sure",
                            participates_in_activities: "Not sure",
                            finds_curriculum_difficult: "Not sure",
                            making_good_progress: "Not sure",
                            gets_school_support: "Not sure",
                            can_talk_feelings: "Not sure",
                            can_calm_down: "Not sure",
                            can_understand_friends: "Not sure",
                            respects_opinions: "Not sure",
                            helps_after_fight: "Not sure",
                            comforts_when_sad: "Not sure"
                        },
                        needs_support: submission.caregiver_observation.needs_support || [],
                        other_support: submission.caregiver_observation.other_support || ""
                    });
                } else {
                    // Foundational stage form fields
                    setObservationForm({
                        resources: submission.caregiver_observation.resources || [],
                        comments: submission.caregiver_observation.comments || ""
                    });
                }
            } else {
                // Reset form for new submissions based on stage
                if (isMid) {
                    setObservationForm({
                        resources: [],
                        other_resources: "",
                        understanding: {
                            motivated_to_learn: "Not sure",
                            follows_schedule: "Not sure",
                            finds_curriculum_difficult: "Not sure",
                            making_good_progress: "Not sure"
                        },
                        school_support_needs: [],
                        other_school_support: "",
                        home_support_plan: ""
                    });
                } else if (isPrep) {
                    setObservationForm({
                        resources: [],
                        other_resources: "",
                        assessment: {
                            finds_school_safe: "Not sure",
                            participates_in_activities: "Not sure",
                            finds_curriculum_difficult: "Not sure",
                            making_good_progress: "Not sure",
                            gets_school_support: "Not sure",
                            can_talk_feelings: "Not sure",
                            can_calm_down: "Not sure",
                            can_understand_friends: "Not sure",
                            respects_opinions: "Not sure",
                            helps_after_fight: "Not sure",
                            comforts_when_sad: "Not sure"
                        },
                        needs_support: [],
                        other_support: ""
                    });
                } else {
                    setObservationForm({
                        resources: [],
                        comments: ""
                    });
                }
            }
        } catch (err) {
            console.error("Error fetching submission:", err);
            if (err.message && (err.message.includes("404") || err.message.toLowerCase().includes('not found'))) {
                setSelectedSubmission(null);
                console.log("No submission found for this assignment, which is expected.");
            } else {
                showNotification(`Failed to fetch submission: ${err.message}`, "error");
            }
        } finally {
            setLoading(false);
        }
    };

    // Fetch attendance overview for a class
    const fetchAttendanceOverview = async (childId, classId) => {
        setLoading(true);
        try {
            const overview = await apiService.getChildAttendanceOverview(childId, classId);
            setSelectedClassAttendance(overview);
        } catch (err) {
            showNotification("Failed to fetch attendance overview.", "error");
            console.error(err);
            setSelectedClassAttendance(null);
        } finally {
            setLoading(false);
        }
    };

    // Handle child selection
    const handleChildSelect = async (child) => {
        setSelectedChild(child);

        // Detect grade when selecting a child
        const grade = detectGrade(child);
        if (grade) {
            setDetectedGrade(grade);
            console.log(`Selected child (${child.name}) with detected grade ${grade}`);
        }

        setCurrentView("childDetail");
    };

    // Handle assignment selection
    const handleAssignmentSelect = (assignment) => {
        setSelectedAssignment(assignment);
        setCurrentView("assignmentDetail");
    };

    // Handle class selection for attendance
    const handleClassSelect = (classItem) => {
        setSelectedClass(classItem);
        setCurrentView("attendanceDetail");
    };

    // Handle link child
    const handleLinkChild = async () => {
        if (!linkEmail.trim()) {
            showNotification("Please enter an email address.", "error");
            return;
        }

        setLoading(true);
        try {
            await apiService.linkChild(linkEmail);
            await fetchChildren(); // Refresh the list of children
            showNotification("Child linked successfully!", "success");
            setIsLinkModalOpen(false);
            setLinkEmail("");
        } catch (err) {
            showNotification(`Failed to link child: ${err.message}`, "error");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Handle back navigation
    const handleBack = () => {
        if (currentView === "assignmentDetail") {
            setCurrentView("childDetail");
            setSelectedAssignment(null);
            setSelectedSubmission(null);

            // Reset form based on stage
            const isPrep = getIsPreparatory();
            const isMid = getIsMiddleStage();

            if (isMid) {
                setObservationForm({
                    resources: [],
                    other_resources: "",
                    understanding: {
                        motivated_to_learn: "Not sure",
                        follows_schedule: "Not sure",
                        finds_curriculum_difficult: "Not sure",
                        making_good_progress: "Not sure"
                    },
                    school_support_needs: [],
                    other_school_support: "",
                    home_support_plan: ""
                });
            } else if (isPrep) {
                setObservationForm({
                    resources: [],
                    other_resources: "",
                    assessment: {
                        finds_school_safe: "Not sure",
                        participates_in_activities: "Not sure",
                        finds_curriculum_difficult: "Not sure",
                        making_good_progress: "Not sure",
                        gets_school_support: "Not sure",
                        can_talk_feelings: "Not sure",
                        can_calm_down: "Not sure",
                        can_understand_friends: "Not sure",
                        respects_opinions: "Not sure",
                        helps_after_fight: "Not sure",
                        comforts_when_sad: "Not sure"
                    },
                    needs_support: [],
                    other_support: ""
                });
            } else {
                setObservationForm({
                    resources: [],
                    comments: ""
                });
            }
        } else if (currentView === "attendanceDetail") {
            setCurrentView("childDetail");
            setSelectedClass(null);
            setSelectedClassAttendance(null);
        } else if (currentView === "childDetail") {
            setCurrentView("dashboard");
            setSelectedChild(null);
            setSelectedChildClasses([]);
            setSelectedChildTeachers([]);
            setSelectedChildProgress(null);
            setSelectedChildAssignments([]);
            setSelectedChildAttendance([]);
            setDetectedGrade(null);
        }
    };

    // Handle resource selection in observation form
    const handleResourceChange = (resource) => {
        setObservationForm(prev => {
            const resources = [...prev.resources];
            if (resources.includes(resource)) {
                return { ...prev, resources: resources.filter(r => r !== resource) };
            } else {
                return { ...prev, resources: [...resources, resource] };
            }
        });
    };

    // Handle assessment changes in observation form
    const handleAssessmentChange = (field, value) => {
        setObservationForm(prev => ({
            ...prev,
            assessment: {
                ...prev.assessment,
                [field]: value
            }
        }));
    };

    // Handle understanding changes for middle stage
    const handleUnderstandingChange = (field, value) => {
        setObservationForm(prev => ({
            ...prev,
            understanding: {
                ...prev.understanding,
                [field]: value
            }
        }));
    };

    // Handle support needs changes in observation form
    const handleSupportChange = (area) => {
        // Different field name based on stage
        const isMid = getIsMiddleStage();
        const fieldName = isMid ? 'school_support_needs' : 'needs_support';

        setObservationForm(prev => {
            const needs = [...prev[fieldName]];
            if (needs.includes(area)) {
                return { ...prev, [fieldName]: needs.filter(n => n !== area) };
            } else {
                return { ...prev, [fieldName]: [...needs, area] };
            }
        });
    };

    // Handle text input changes in observation form
    const handleCommentsChange = (e) => {
        const { name, value } = e.target;
        setObservationForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle observation form submission
    const handleObservationSubmit = async (e) => {
        e.preventDefault();

        if (observationForm.resources.length === 0) {
            showNotification("Please select at least one resource.", "error");
            return;
        }

        setLoading(true);
        try {
            // Use the detected grade to determine API service
            const gradeToUse = detectedGrade;
            const serviceToUse = getApiServiceForGrade(gradeToUse, selectedChild, selectedAssignment);
            const isPrep = isPreparatoryGrade(gradeToUse);
            const isMid = isMiddleStageGrade(gradeToUse);

            console.log(`Submitting observation using ${isMid ? 'middle' : isPrep ? 'preparatory' : 'foundation'} API service for grade ${gradeToUse}`);

            const result = await serviceToUse.addCaregiverObservation(
                selectedChild.student_id || selectedChild.id,
                selectedAssignment.assignment_id || selectedAssignment.id,
                observationForm
            );

            setSelectedSubmission(prev => ({
                ...prev,
                caregiver_observation: result.caregiver_observation || result
            }));

            showNotification("Caregiver observation submitted successfully!", "success");
        } catch (err) {
            showNotification(`Failed to submit observation: ${err.message}`, "error");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Show notification
    const showNotification = (message, type = "success") => {
        setNotification({
            show: true,
            message,
            type
        });

        // Hide notification after 3 seconds
        setTimeout(() => {
            setNotification({ show: false, message: "", type: "" });
        }, 3000);
    };

    // Handle message composition
    const handleComposeMessage = (e) => {
        e.preventDefault();
        showNotification("Message sent!");
        // Additional logic for message sending would go here
    };

    // Get parent initials for profile picture fallback
    const getParentInitials = () => {
        // You might want to get this from user data or context
        // For now, return a default
        return "PA"; // Parent Avatar
    };

    // Render the appropriate view based on currentView state
    if (currentView === "dashboard") {
        return (
            <Layout pageTitle="Parent Dashboard">
                <DashboardView
                    children={children}
                    filteredChildren={getFilteredChildren()}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    handleChildSelect={handleChildSelect}
                    isLinkModalOpen={isLinkModalOpen}
                    setIsLinkModalOpen={setIsLinkModalOpen}
                    linkEmail={linkEmail}
                    setLinkEmail={setLinkEmail}
                    handleLinkChild={handleLinkChild}
                    loading={loading}
                    error={error}
                    API_BASE_URL={API_BASE_URL}
                    // Parent profile picture props
                    parentProfilePicture={parentProfilePicture}
                    showParentProfilePictureModal={showParentProfilePictureModal}
                    setShowParentProfilePictureModal={setShowParentProfilePictureModal}
                    handleParentProfilePictureUpload={handleParentProfilePictureUpload}
                    uploadingParentPicture={uploadingParentPicture}
                    getParentInitials={getParentInitials}
                />
                <Notification
                    notification={notification}
                    setNotification={setNotification}
                />

                {/*/!* Parent Profile Picture Modal *!/*/}
                {/*{showParentProfilePictureModal && (*/}
                {/*    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" style={{ fontFamily: 'Poppins, sans-serif' }}>*/}
                {/*        <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 shadow-2xl">*/}
                {/*            <h3 className="text-lg font-semibold text-gray-900 mb-4">Update Profile Picture</h3>*/}

                {/*            <div className="text-center mb-6">*/}
                {/*                <div className="w-24 h-24 mx-auto mb-4">*/}
                {/*                    {parentProfilePicture ? (*/}
                {/*                        <img*/}
                {/*                            src={parentProfilePicture}*/}
                {/*                            alt="Current profile"*/}
                {/*                            className="w-full h-full rounded-full object-cover border-4 border-orange-200"*/}
                {/*                        />*/}
                {/*                    ) : (*/}
                {/*                        <div className="w-full h-full rounded-full bg-orange-100 flex items-center justify-center border-4 border-orange-200">*/}
                {/*                            <span className="text-xl font-semibold text-orange-600">*/}
                {/*                                {getParentInitials()}*/}
                {/*                            </span>*/}
                {/*                        </div>*/}
                {/*                    )}*/}
                {/*                </div>*/}
                {/*                <p className="text-sm text-gray-600">*/}
                {/*                    Choose a new profile picture (Max 5MB, JPEG/PNG/GIF)*/}
                {/*                </p>*/}
                {/*            </div>*/}

                {/*            <div className="space-y-4">*/}
                {/*                <input*/}
                {/*                    type="file"*/}
                {/*                    accept="image/*"*/}
                {/*                    onChange={(e) => handleParentProfilePictureUpload(e.target.files[0])}*/}
                {/*                    disabled={uploadingParentPicture}*/}
                {/*                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100 file:cursor-pointer cursor-pointer disabled:opacity-50"*/}
                {/*                />*/}

                {/*                <div className="flex space-x-3">*/}
                {/*                    <button*/}
                {/*                        onClick={() => setShowParentProfilePictureModal(false)}*/}
                {/*                        disabled={uploadingParentPicture}*/}
                {/*                        className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"*/}
                {/*                    >*/}
                {/*                        Cancel*/}
                {/*                    </button>*/}

                {/*                    {uploadingParentPicture && (*/}
                {/*                        <div className="flex-1 flex items-center justify-center">*/}
                {/*                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-orange-500 border-t-transparent"></div>*/}
                {/*                            <span className="ml-2 text-sm text-gray-600">Uploading...</span>*/}
                {/*                        </div>*/}
                {/*                    )}*/}
                {/*                </div>*/}
                {/*            </div>*/}
                {/*        </div>*/}
                {/*    </div>*/}
                {/*)}*/}
            </Layout>
        );
    }

    if (currentView === "childDetail" && selectedChild) {
        return (
            <Layout pageTitle={`Parent Dashboard: ${selectedChild.name}`}>
                <ChildDetailView
                    selectedChild={selectedChild}
                    childProfilePicture={childProfilePicture}
                    selectedChildClasses={selectedChildClasses}
                    selectedChildTeachers={selectedChildTeachers}
                    selectedChildProgress={selectedChildProgress}
                    selectedChildAssignments={selectedChildAssignments}
                    selectedChildAttendance={selectedChildAttendance}
                    handleBack={handleBack}
                    handleClassSelect={handleClassSelect}
                    handleAssignmentSelect={handleAssignmentSelect}
                    showProfilePictureModal={showProfilePictureModal}
                    setShowProfilePictureModal={setShowProfilePictureModal}
                    handleProfilePictureUpload={handleProfilePictureUpload}
                    uploadingPicture={uploadingPicture}
                    loading={loading}
                    error={error}
                    API_BASE_URL={API_BASE_URL}
                    // Pass parent profile picture props to child detail view if needed
                    parentProfilePicture={parentProfilePicture}
                    getParentInitials={getParentInitials}
                />
                <Notification
                    notification={notification}
                    setNotification={setNotification}
                />
            </Layout>
        );
    }

    if (currentView === "assignmentDetail" && selectedChild && selectedAssignment) {
        // Get stage status
        const isPreparatory = isPreparatoryGrade(detectedGrade);
        const isMiddleStage = isMiddleStageGrade(detectedGrade);
        console.log(`Rendering AssignmentDetailView with isPreparatory=${isPreparatory}, isMiddleStage=${isMiddleStage}, grade=${detectedGrade}`);

        return (
            <Layout pageTitle={`Assignment: ${selectedAssignment.title || "Details"}`}>
                <AssignmentDetailView
                    selectedChild={{
                        ...selectedChild,
                        classes: selectedChildClasses, // Pass classes for grade determination
                        grade: detectedGrade // Pass the detected grade directly
                    }}
                    assignments={selectedChildAssignments}
                    selectedAssignment={selectedAssignment}
                    selectedSubmission={selectedSubmission} // *** FIX: Pass selectedSubmission as a prop ***
                    detectedGrade={detectedGrade}
                    handleBack={handleBack}

                    // Caregiver observation form props
                    observationForm={observationForm}
                    setObservationForm={setObservationForm}
                    handleResourceChange={handleResourceChange}
                    handleCommentsChange={handleCommentsChange}
                    handleObservationSubmit={handleObservationSubmit}

                    // Additional handlers for preparatory and middle stages
                    handleAssessmentChange={handleAssessmentChange}
                    handleSupportChange={handleSupportChange}
                    handleUnderstandingChange={handleUnderstandingChange}

                    // Navigation and UI state
                    loading={loading}
                    error={error}

                    // Pass stage flags
                    isPreparatory={isPreparatory}
                    isMiddleStage={isMiddleStage}

                    // Pass stage-specific API services
                    apiService={apiService}
                    preparatoryApiService={preparatoryApiService}
                    middleApiService={middleApiService}

                    // Pass parent profile picture props if needed
                    parentProfilePicture={parentProfilePicture}
                    getParentInitials={getParentInitials}

                    // Call this when new caregiver observations are submitted
                    onCaregiverObservationComplete={() => {
                        // Refresh submission data
                        fetchSubmission(
                            selectedChild.student_id || selectedChild.id,
                            selectedAssignment.assignment_id || selectedAssignment.id
                        );
                    }}
                />
                <Notification
                    notification={notification}
                    setNotification={setNotification}
                />
            </Layout>
        );
    }

    if (currentView === "attendanceDetail" && selectedChild && selectedClass) {
        return (
            <Layout pageTitle={`Attendance: ${selectedClass.name}`}>
                <AttendanceDetailView
                    selectedChild={selectedChild}
                    selectedClass={selectedClass}
                    selectedClassAttendance={selectedClassAttendance}
                    selectedChildAttendance={selectedChildAttendance}
                    handleBack={handleBack}
                    loading={loading}
                    error={error}
                    // Pass parent profile picture props if needed
                    parentProfilePicture={parentProfilePicture}
                    getParentInitials={getParentInitials}
                />
                <Notification
                    notification={notification}
                    setNotification={setNotification}
                />
            </Layout>
        );
    }

    // Fallback view (shouldn't happen)
    return (
        <Layout pageTitle="Parent Dashboard">
            <div className="min-h-screen font-[poppins] flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <h2 className="text-xl font-semibold text-gray-900">Something went wrong</h2>
                    <p className="mt-2 text-gray-600">Unable to load dashboard.</p>
                    <button
                        onClick={() => setCurrentView("dashboard")}
                        className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#D67C2C] hover:bg-[#1a5b94]"
                    >
                        Return to Dashboard
                    </button>
                </div>
            </div>
            <Notification
                notification={notification}
                setNotification={setNotification}
            />
        </Layout>
    );
};

export default ParentDashboard;